import { useMemo } from "react";
import { IStatus } from "entities/RCA/StatusEntity/StatusEntityInterfaces";
import { IPriority } from "entities/RCA/PriorityEntity/PriorityEntityInterfaces";
import { useTranslation } from "react-i18next";
import { useRCAHistoryMainStyles } from "pages/Corporate/RCA/RCA.styles";
import {
    RCADueDateLog,
    RCAUserChangeLog,
    RCAStatusChangeLog,
    RCAPriorityChangeLog,
    RCATextChangeLog,
    RCAHistoryLog,
    IHistoryLogBase,
    FieldNameEnumsActionPlan,
    ChangeTypeEnums,
} from "pages/Corporate/RCA/components/RCAHistoryLogsComponents";

export const RCAActionPlanHistoryLogItem = ({
    log,
    statuses,
    priorities,
}: {
    log: IHistoryLogBase;
    statuses: IStatus[];
    priorities: IPriority[];
}) => {
    const classes = useRCAHistoryMainStyles();
    const { t } = useTranslation();

    const changeLog = useMemo(() => {
        if (log.field_name === FieldNameEnumsActionPlan.DUE_DATE) {
            return <RCADueDateLog newValue={log.new_value} oldValue={log.old_value} />;
        } else if (log.field_name === FieldNameEnumsActionPlan.ASSIGNEE_ID) {
            return <RCAUserChangeLog newValue={log.new_value} oldValue={log.old_value} />;
        } else if (log.field_name === FieldNameEnumsActionPlan.STATUS_ID) {
            const oldStatus = statuses.find((status) => status.status_id === Number(log.old_value));
            const newStatus = statuses.find((status) => status.status_id === Number(log.new_value));
            return <RCAStatusChangeLog oldStatus={oldStatus} newStatus={newStatus} />;
        } else if (log.field_name === FieldNameEnumsActionPlan.PRIORITY_ID) {
            const oldPriority = priorities.find((priority) => priority.priority_id === Number(log.old_value));
            const newPriority = priorities.find((priority) => priority.priority_id === Number(log.new_value));
            return <RCAPriorityChangeLog oldPriority={oldPriority} newPriority={newPriority} />;
        } else if (
            log.field_name === FieldNameEnumsActionPlan.NAME ||
            log.field_name === FieldNameEnumsActionPlan.DESCRIPTION
        ) {
            return <RCATextChangeLog newValue={log.new_value} oldValue={log.old_value} />;
        } else if (log.field_name === FieldNameEnumsActionPlan.ACTION_PLAN_TASK_COMPLETED_AT) {
            return (
                <div className={classes.historyChangeWrapper}>
                    <span className={classes.historyText}>
                        {!log.old_value
                            ? t("rca.history_audit_logs.checked_completion")
                            : t("rca.history_audit_logs.unchecked_completion")}
                    </span>
                </div>
            );
        }
    }, [classes, log, statuses, priorities, t]);

    const changedField = useMemo(() => {
        if (log.field_name === FieldNameEnumsActionPlan.DUE_DATE) {
            return t("rca.history_audit_logs.due_date");
        } else if (log.field_name === FieldNameEnumsActionPlan.ASSIGNEE_ID) {
            return t("rca.history_audit_logs.owner");
        } else if (log.field_name === FieldNameEnumsActionPlan.STATUS_ID) {
            return t("rca.history_audit_logs.status");
        } else if (log.field_name === FieldNameEnumsActionPlan.PRIORITY_ID) {
            return t("rca.history_audit_logs.priority");
        } else if (log.field_name === FieldNameEnumsActionPlan.NAME) {
            return t("rca.history_audit_logs.name");
        } else if (log.field_name === FieldNameEnumsActionPlan.DESCRIPTION) {
            return t("rca.history_audit_logs.description");
        } else if (
            log.field_name === FieldNameEnumsActionPlan.ACTION_PLAN_TASK_COMPLETED_AT ||
            log.field_name === FieldNameEnumsActionPlan.ACTION_PLAN_TASK_DELETED_AT ||
            log.field_name === FieldNameEnumsActionPlan.ACTION_PLAN_TASK_DESCRIPTION
        ) {
            return t("rca.history_audit_logs.task");
        } else if (
            log.field_name === FieldNameEnumsActionPlan.CREATED_AT ||
            log.field_name === FieldNameEnumsActionPlan.DELETED_AT
        ) {
            return t("rca.history_audit_logs.action_plan");
        } else if (
            log.field_name === FieldNameEnumsActionPlan.ACTION_PLAN_MEDIA_CREATED_AT ||
            log.field_name === FieldNameEnumsActionPlan.ACTION_PLAN_MEDIA_DELETED_AT
        ) {
            return t("rca.history_audit_logs.media");
        } else if (log.field_name === FieldNameEnumsActionPlan.MEETING_TYPE_ID) {
            return t("rca.history_audit_logs.meeting_type");
        }
        return "";
    }, [log, t]);

    const changeType = useMemo(() => {
        if (ChangeTypeEnums.INSERT === log.change_type) {
            return (
                <>
                    {t("rca.history_audit_logs.creation")} <strong>{changedField}</strong>
                </>
            );
        } else if (ChangeTypeEnums.UPDATE === log.change_type) {
            return (
                <>
                    {t("rca.history_audit_logs.updated_field")} <strong>{changedField}</strong>
                </>
            );
        } else if (ChangeTypeEnums.DELETE === log.change_type) {
            return (
                <>
                    {t("rca.history_audit_logs.deletion")} <strong>{changedField}</strong>
                </>
            );
        }
    }, [log, changedField, t]);

    return (
        <RCAHistoryLog
            changeType={changeType}
            changeLog={changeLog}
            changeDate={log.changed_at}
            changeUser={log.user}
        />
    );
};
